import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import { CheckIcon, CircleCheckIcon, SearchIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Modal, ModalContent, ModalHeader } from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { countries } from "utils/countries";
import { useCurrentUser } from "utils/currentUser";

import type { CreditsafeSearchCompany } from "components/innointel/types";

const CompanySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  .input-area {
    display: flex;
    flex-direction: column;
    > span {
      display: block;
      font-size: 12px;
      font-weight: 600;
    }
    &:not(:last-of-type) {
      margin-bottom: 15px;
    }
  }
`;

type CreditsafeManualMatchProps = {
  companyId: string;
  name: string;
  location?: OpenAPI.Schemas["InnointelCompanies"]["location"];
  isOpen?: boolean;
  onClose?: () => void;
  onError?: (errorMessage: string) => void;
};

const CreditsafeManualMatch = ({
  companyId,
  isOpen,
  onClose,
  name: propName,
  location: propLocation,
  onError,
}: CreditsafeManualMatchProps) => {
  const user = useCurrentUser();
  const organisationId = user?.ownerOrganisation?._id;
  const [name, setName] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [initialSearchDone, setInitialSearchDone] = useState<boolean>(false);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState<string>("");
  const [companies, setCompanies] = useState<CreditsafeSearchCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");

  const searchCompanies = useCallback(
    (newName = "", newLocation = "") => {
      if (!organisationId) {
        return;
      }
      setLoadingCompanies(true);
      setSelectedCompany("");
      if (newName) {
        setName(newName);
      }
      if (newLocation) {
        setLocation(newLocation);
      }
      api.verifications.searchForCompanies(
        newLocation || location,
        "",
        newName || name,
        "org",
        organisationId,
        ({ companies: newCompanies }) => {
          setCompanies(newCompanies ?? []);
          setLoadingCompanies(false);
        },
        (err) => {
          toast.error(err.message);
          setLoadingCompanies(false);
          onError?.(err.message);
        },
      );
    },
    [location, name, onError, organisationId],
  );

  const assignCreditsafeInformation = useCallback(() => {
    setLoadingReport(true);
    api.innointel.assignCreditsafeId(
      companyId,
      { creditsafeId: selectedCompany },
      () => {
        api.invalidateQueries(api.innointel.getCompany, [companyId]);
        onClose?.();
        setLoadingReport(false);
      },
      () => {
        onClose?.();
        setLoadingReport(false);
      },
    );
  }, [companyId, selectedCompany, onClose]);

  useEffect(() => {
    if (isOpen && !initialSearchDone) {
      setInitialSearchDone(true);
      let newLocation = "";
      setName(propName);
      if (propLocation?.search?.address?.iso2) {
        const countryIso2 = propLocation.search.address.iso2;
        newLocation = countryIso2;
        setLocation(countryIso2);
      } else if (propLocation?.search?.address?.country) {
        const countryNameLowerCase =
          propLocation.search.address.country.toLowerCase();
        const countryIso2 = countries.find(
          (country) =>
            country.name.toLowerCase().indexOf(countryNameLowerCase) > -1,
        )?.iso2;
        if (countryIso2) {
          newLocation = countryIso2;
          setLocation(countryIso2);
        }
      }
      if (propName && newLocation) {
        searchCompanies(propName, newLocation);
      }
    }
  }, [propName, propLocation, searchCompanies, initialSearchDone, isOpen]);

  const filteredCountries = useMemo(() => {
    const searchTerm = countrySearchTerm.toLowerCase();
    return countries.filter((country) =>
      country.name.toLowerCase().includes(searchTerm),
    );
  }, [countrySearchTerm]);

  const CountryPartialMatchIcon = useMemo(
    () => (
      <Popup
        on="hover"
        trigger={
          <Icon
            icon={CircleCheckIcon}
            color="green"
            style={{ marginLeft: 5 }}
          />
        }
      >
        This piece of information matches the original company
      </Popup>
    ),
    [],
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalHeader>
        <p>Find credit information for {propName}</p>
        <p style={{ fontWeight: 400, fontSize: 14 }}>
          We couldn&apos;t find credit information for this company
          automatically - Please search for an option below to link credit
          information.
        </p>
      </ModalHeader>
      <ModalContent scrolling>
        <CompanySearchContainer>
          <div className="input-area">
            <span>Company name</span>
            <Input
              value={name}
              onChange={(_e, { value }) => {
                setName(value);
              }}
              fluid
            />
          </div>

          <div className="input-area">
            <span>Country</span>
            <Dropdown
              fluid
              variant="outline"
              value={location}
              onChange={(_e, { value }) => {
                setLocation(String(value));
                setCountrySearchTerm("");
              }}
              searchQuery={countrySearchTerm}
              search
              onSearchChange={(_e, { searchQuery }) =>
                setCountrySearchTerm(searchQuery)
              }
              options={filteredCountries.map((country) => ({
                key: country.iso2,
                value: country.iso2,
                text: country.name,
              }))}
            />
          </div>

          <div className="input-area">
            <Button
              fluid
              color={
                !selectedCompany
                  ? "primary"
                  : selectedCompany
                    ? "green"
                    : undefined
              }
              onClick={
                selectedCompany
                  ? () => assignCreditsafeInformation()
                  : () => searchCompanies()
              }
              loading={loadingCompanies || loadingReport}
              disabled={loadingCompanies || loadingReport}
            >
              <Icon icon={selectedCompany ? CheckIcon : SearchIcon} />
              {selectedCompany
                ? "Get credit information for this result"
                : "Search"}
            </Button>
          </div>

          {companies.length > 0 ? (
            <Table selectable>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Company name</TableHeaderCell>
                  <TableHeaderCell>Address</TableHeaderCell>
                  <TableHeaderCell>Reg no</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHeader>

              <TableBody>
                {companies.map((company) => (
                  <TableRow
                    key={company.id ?? company._id}
                    onClick={() =>
                      setSelectedCompany(
                        selectedCompany === company.id
                          ? ""
                          : (company.id ?? ""),
                      )
                    }
                    style={{ cursor: "pointer" }}
                    active={selectedCompany === company.id}
                  >
                    <TableCell>
                      {company.name}
                      {company.name?.toLowerCase() === name.toLowerCase()
                        ? CountryPartialMatchIcon
                        : null}
                    </TableCell>
                    <TableCell>
                      {company.address?.simpleValue}
                      {company.address?.postCode?.toLowerCase() ===
                      propLocation?.search?.address?.postcode?.toLowerCase()
                        ? CountryPartialMatchIcon
                        : null}
                    </TableCell>
                    <TableCell>{company.regNo}</TableCell>
                    <TableCell>
                      {company.statusDescription ?? company.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CompanySearchContainer>
      </ModalContent>
    </Modal>
  );
};

export default CreditsafeManualMatch;
